/**
 * StatCard Component
 * Displays a statistic card with label, value, and growth indicator
 */

import { Link } from 'react-router-dom';

interface StatCardProps {
  label: string;
  value: string | number;
  linkTo?: string;
  icon?: React.ReactNode;
  growthIndicator?: {
    text: string;
    type: 'positive' | 'negative' | 'neutral' | 'placeholder';
  };
  change?: {
    value: number;
    type: 'count' | 'percent';
  };
}

export const StatCard = ({ label, value, linkTo, icon, growthIndicator, change }: StatCardProps) => {
  const getGrowthColor = () => {
    if (!growthIndicator) return '';
    switch (growthIndicator.type) {
      case 'positive':
        return 'text-green-600';
      case 'negative':
        return 'text-red-600';
      case 'placeholder':
        return '';
      default:
        return 'text-meta';
    }
  };

  const getChangeBadgeClasses = () => {
    if (!change) return '';
    if (change.value > 0) return 'bg-status-success text-status-success';
    if (change.value < 0) return 'bg-status-danger text-status-danger';
    return 'bg-surface-secondary text-meta';
  };

  const formatChangeValue = () => {
    if (!change) return '';
    const prefix = change.value > 0 ? '+' : '';
    const suffix = change.type === 'percent' ? '%' : '';
    return `${prefix}${change.value}${suffix}`;
  };

  return (
    <div className="bg-surface rounded-lg border border-strong shadow px-6 py-5 flex items-center justify-center">
      {/* Icon on left - dark grey icon on light gray circle */}
      {icon && (
        <div className="flex-shrink-0 mr-6">
          <div className="h-14 w-14 rounded-full bg-surface-secondary flex items-center justify-center text-blue-600">
            {icon}
          </div>
        </div>
      )}

      {/* Text content on right */}
      <div className="flex flex-col items-start justify-center">
        {/* Top line: Label (as link if linkTo provided) */}
        {linkTo ? (
          <Link to={linkTo} className="text-sm text-meta hover:text-blue-600 dark:hover:text-blue-400 mb-0.5">
            {label}
          </Link>
        ) : (
          <div className="text-sm text-meta mb-0.5">{label}</div>
        )}

        {/* Middle line: Number (large font) with optional change badge */}
        <div className="flex items-center gap-2">
          <div className="text-3xl font-bold text-heading">{value}</div>
          {change && (
            <span className={`px-2 py-0.5 rounded text-sm font-medium ${getChangeBadgeClasses()}`}>
              {formatChangeValue()}
            </span>
          )}
        </div>

        {/* Bottom line: Growth indicator */}
        {growthIndicator && (
          <div className={`text-sm ${getGrowthColor()}`}>
            {growthIndicator.type === 'placeholder' ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-surface-secondary text-meta">
                {growthIndicator.text}
              </span>
            ) : (
              growthIndicator.text
            )}
          </div>
        )}
      </div>
    </div>
  );
};
